import React from 'react';
import AddIcon from '@material-ui/icons/Add';
import CheckIcon from '@material-ui/icons/Check';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { DashboardColumnKind, DashboardRole } from './types';
import {
  ChipRow,
  DetailHeading,
  DetailSection,
  ExpandButton,
  GrantedChip,
  PermissionChip,
  RequestAccessButton,
  RoleCopy,
  RoleDescription,
  RoleDetails,
  RoleHeader,
  RoleName,
  RoleRow,
} from './styles';

interface DashboardRoleRowProps {
  role: DashboardRole;
  kind: DashboardColumnKind;
  isExpanded: boolean;
  onToggle: () => void;
  isAwaitingApproval?: boolean;
  onRequestAccess?: (role: DashboardRole) => void;
}

export function DashboardRoleRow({
  role,
  kind,
  isExpanded,
  onToggle,
  isAwaitingApproval = false,
  onRequestAccess,
}: DashboardRoleRowProps) {
  const hasDetails = role.permissions.length > 0 || role.modifiers.length > 0;

  return (
    <RoleRow>
      <RoleHeader className={isExpanded ? 'expanded' : undefined}>
        <ExpandButton
          type="button"
          aria-expanded={isExpanded}
          aria-label={
            isExpanded ? `Collapse ${role.name}` : `Expand ${role.name}`
          }
          onClick={onToggle}
          disabled={!hasDetails}
        >
          {isExpanded ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </ExpandButton>
        <RoleCopy>
          <RoleName>{role.name}</RoleName>
          <RoleDescription>{role.description}</RoleDescription>
        </RoleCopy>
        {kind === 'granted' && (
          <GrantedChip icon={<CheckIcon />} label="Granted" />
        )}

        {role.canRequestAccess && (
          <RequestAccessButton
            color="primary"
            variant="contained"
            disabled={isAwaitingApproval}
            startIcon={isAwaitingApproval ? undefined : <AddIcon />}
            onClick={() => onRequestAccess?.(role)}
          >
            {isAwaitingApproval ? 'Awaiting approval' : 'Request Access'}
          </RequestAccessButton>
        )}
      </RoleHeader>
      {isExpanded && hasDetails && (
        <RoleDetails>
          {role.permissions.length > 0 && (
            <DetailSection>
              <DetailHeading>Permissions</DetailHeading>
              <ChipRow>
                {role.permissions.map(permission => (
                  <PermissionChip key={permission} label={permission} />
                ))}
              </ChipRow>
            </DetailSection>
          )}
          {role.modifiers.length > 0 && (
            <DetailSection>
              <DetailHeading>Modifiers</DetailHeading>
              <ChipRow>
                {role.modifiers.map(modifier => (
                  <PermissionChip key={modifier} label={modifier} />
                ))}
              </ChipRow>
            </DetailSection>
          )}
        </RoleDetails>
      )}
    </RoleRow>
  );
}
